import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { useTranslation } from "react-i18next";
import styled, { withTheme } from "styled-components";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useEffect, useState, useCallback } from "react";
import ReactPlaceholder from "react-placeholder";
import { TextBlock } from "react-placeholder/lib/placeholders";
import { connect } from "react-redux";
import Paper from "../../ui-library/Paper/Paper";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import Targets from "../../library/Goals/Targets";
import SetDailyGoals from "../../library/SetDailyGoals/SetDailyGoals";
import {
  targetsReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./Targets.store";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import { validDateString } from "../../../modules/utils";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";
import SmartDailyTargetsHistory from "../DailyTargetsHistory/DailyTargetsHistory";
import { studyStatusesGSDIII } from "../../../modules/constants";

const StyledFooter = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  justify-content: flex-end;

  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

export const SmartTargets = (props) => {
  const {
    goalsList,
    canViewTargetsHistory,
    canCreateNewTarget,
    status,
    fetchGoals,
    userData,
    dateRange: { startDate = null, endDate = null } = {},
    resetState,
    participantId,
    updateDailyTargets,
    currentDailyTargets,
    update,
    studyId,
  } = props;

  const { t } = useTranslation();

  const [validateDailyTargets, setValidateDailyTargets] = useState(false);
  const [showUpdateDailyTargets, setShowUpdateDailyTargets] = useState(false);
  const [dailyTargets, setDailyTargets] = useState(null);
  const [showDailyTargetHistory, setShowDailyTargetHistory] = useState(false);

  const submitForm = useCallback(() => {
    updateDailyTargets(
      participantId,
      dailyTargets,
      dailyTargets.reasonForChange
    )
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        closeTargetsModal();
        setDailyTargets(null);
        fetchGoals(
          startDate,
          endDate,
          participantId,
          studyId,
          userData?.studyStatus,
          t
        );
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  }, [
    dailyTargets,
    endDate,
    fetchGoals,
    participantId,
    startDate,
    studyId,
    updateDailyTargets,
    userData,
    t,
  ]);

  const validadeBeforeSubmit = () => {
    setValidateDailyTargets(true);
  };

  useEffect(() => {
    if (dailyTargets) {
      setValidateDailyTargets(false);
      submitForm();
    }
  }, [dailyTargets, submitForm]);

  const onValidate = useCallback((valid, goals) => {
    if (valid) {
      setDailyTargets(goals);
    } else {
      setDailyTargets(null);
      setValidateDailyTargets(false);
    }
  }, []);

  useEffect(() => {
    if (
      validDateString(startDate) &&
      validDateString(endDate) &&
      userData?.studyStatus
    ) {
      fetchGoals(
        startDate,
        endDate,
        participantId,
        studyId,
        userData.studyStatus,
        t
      );
    }
  }, [endDate, fetchGoals, startDate, participantId, studyId, userData, t]);

  useEffect(() => {
    if (
      update &&
      validDateString(startDate) &&
      validDateString(endDate) &&
      userData?.studyStatus
    ) {
      fetchGoals(
        startDate,
        endDate,
        participantId,
        studyId,
        userData.studyStatus,
        t
      );
    }
  }, [
    endDate,
    fetchGoals,
    participantId,
    startDate,
    studyId,
    update,
    userData,
    t,
  ]);

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const closeTargetsModal = () => {
    setShowUpdateDailyTargets(false);
  };

  const closeHistoryModal = () => {
    setShowDailyTargetHistory(false);
  };

  return (
    <ReactPlaceholder
      showLoadingAnimation={true}
      type={"text"}
      ready={status}
      customPlaceholder={<SmartTargetPlaceHolder {...{ props }} />}
    >
      {goalsList && userData?.studyStatus && (
        <Targets
          goalsList={Object.values(goalsList)}
          {...{ canCreateNewTarget }}
          {...{ canViewTargetsHistory }}
          onEdit={() => setShowUpdateDailyTargets(true)}
          onViewHistory={() => setShowDailyTargetHistory(true)}
          studyStatus={userData.studyStatus}
        />
      )}

      <CustomModal
        show={showUpdateDailyTargets}
        title={`Daily target for ${userData?.participantUgnxId ?? ""}`}
        onClose={closeTargetsModal}
      >
        <SetDailyGoals
          validate={validateDailyTargets}
          {...{ onValidate }}
          editing={true}
          default
          defaultTargets={currentDailyTargets}
        />
        <StyledFooter>
          <PrimaryButton
            data-testid="save-targets-button"
            onClick={() => validadeBeforeSubmit()}
          >
            Create new target
          </PrimaryButton>
        </StyledFooter>
      </CustomModal>

      <CustomModal
        show={showDailyTargetHistory}
        title={`${t("contentText.dietDiary.targets.history.title")} ${
          userData?.participantUgnxId ?? ""
        }`}
        onClose={closeHistoryModal}
      >
        <SmartDailyTargetsHistory
          dailyTargetId={currentDailyTargets?.key}
          onClose={closeHistoryModal}
        />
      </CustomModal>
    </ReactPlaceholder>
  );
};

const SmartTargetPlaceHolder = (props) => {
  const { userData } = props;

  const { t } = useTranslation();

  return (
    <Paper
      header={
        userData?.studyStatus
          ? userData.studyStatus === studyStatusesGSDIII.SAD_PERIOD.id
            ? t("contentText.dietDiary.targets.title.sadPeriod")
            : t("contentText.dietDiary.targets.title")
          : ""
      }
    >
      <TextBlock rows={11} color="rgba(0,0,0,.15)" />
    </Paper>
  );
};

export default withTheme(
  dynamic(
    "targetsReducer",
    subspaced({
      // We map extra state because we might need to acess other stores for data, subspace is self contained.
      mapExtraState: (state, rootState) => ({
        loginReducer: state.loginReducer,
      }),
    }),
    attachReducer(targetsReducer)
  )(connect(mapStateToProps, mapDispatchToProps)(SmartTargets))
);
